import React, { useEffect, useState, useCallback } from "react";
import "../Css/map.css";
import MapNav from "./MapNav";
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, useMap, } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import Logger from "./Logger";
import { getLoggerBaseURL } from "../utils/getLoggerBaseURL";

// Initialize Logger
const logger = new Logger(getLoggerBaseURL());

// ─── Constants ────────────────────────────────────────────────────────────────

const ROUTE_COLORS = [
    "#e63946", "#1d3557", "#2a9d8f",
    "#f4a261", "#6a4c93", "#ff006e", "#3a86ff",
];

const VEHICLE_ICONS = {
    BIKE: "https://cdn-icons-png.flaticon.com/512/2972/2972185.png",
    CAR: "https://cdn-icons-png.flaticon.com/512/744/744465.png",
    DEFAULT: "https://cdn-icons-png.flaticon.com/512/1946/1946429.png",
};

// OSRM /route config  (NOT /match — route always follows real roads)
const OSRM_WAYPOINTS_PER_CALL = 50;  // route API supports up to 100; 50 is safe
const OSRM_MAX_WAYPOINTS = 40;  // thin GPS down to this before routing

// GPS cleaning thresholds
const MIN_MOVE_KM = 0.005;  // ignore jitter under 5 m
const MAX_JUMP_KM = 0.50;   // ignore teleports over 500 m
const MAX_SPEED_KH = 120;    // ignore points implying > 120 km/h
const MAX_SPIKE_KM = 0.15;   // spike leg must be under 150 m to be considered noise

// ─── Leaflet icons ────────────────────────────────────────────────────────────

const getDriverIcon = (vehicleType, color) =>
    L.divIcon({
        className: "",
        html: `
            <div style="position:relative;width:36px;height:36px;">
                <div style="
                    position:absolute;top:50%;left:50%;
                    transform:translate(-50%,-50%);
                    width:36px;height:36px;border-radius:50%;
                    background:${color}22;border:2px solid ${color};
                    animation:driverPulse 1.8s ease-out infinite;
                "></div>
                <div style="
                    position:absolute;top:50%;left:50%;
                    transform:translate(-50%,-50%);
                    width:24px;height:24px;border-radius:50%;
                    background:white;border:2px solid ${color};
                    display:flex;align-items:center;justify-content:center;
                    box-shadow:0 2px 8px rgba(0,0,0,.25);overflow:hidden;
                ">
                    <img src="${VEHICLE_ICONS[vehicleType] ?? VEHICLE_ICONS.DEFAULT}"
                         width="14" height="14" />
                </div>
            </div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
    });

const startIcon = L.divIcon({
    className: "",
    html: `<div style="
        width:12px;height:12px;border-radius:50%;
        background:#10b981;border:2px solid white;
        box-shadow:0 2px 6px rgba(0,0,0,.3);
    "></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
});

// Inject pulse animation once
if (typeof document !== "undefined" && !document.getElementById("driver-pulse-style")) {
    const s = document.createElement("style");
    s.id = "driver-pulse-style";
    s.textContent = `
        @keyframes driverPulse {
            0%   { transform:translate(-50%,-50%) scale(1);   opacity:.8; }
            100% { transform:translate(-50%,-50%) scale(2.2); opacity:0;  }
        }`;
    document.head.appendChild(s);
}

// ─── Map helpers ──────────────────────────────────────────────────────────────

/** Auto-fit map bounds whenever the drivers list changes. */
const FitBounds = ({ drivers }) => {
    const map = useMap();
    useEffect(() => {
        const points = drivers.flatMap((d) => d.snappedPath ?? []);
        if (points.length) map.fitBounds(points, { padding: [50, 50], maxZoom: 14 });
    }, [drivers, map]);
    return null;
};

// ─── Geometry utilities ───────────────────────────────────────────────────────

const toRad = (deg) => (deg * Math.PI) / 180;

/** Haversine distance in km. */
const distKm = (a, b) => {
    const R = 6371;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const h =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

/**
 * Remove GPS noise from a chronologically sorted point list.
 *
 * Filters applied in order:
 *   1. Sub-5 m jitter                         → skip
 *   2. > 500 m jump                           → skip (teleport / GPS glitch)
 *   3. Implied speed > 120 km/h               → skip
 *   4. Spike / triangle pattern (look-ahead)  → skip
 *      curr is a noise spike when:
 *        - prev→curr and curr→next are both short (< 150 m each)
 *        - but the detour ratio is high: (d1 + d2) > 2.5 × direct prev→next
 *      This catches the "arrow" artifacts visible in the GPS trace.
 */
const cleanGPSPoints = (points) => {
    if (!points?.length) return [];

    const valid = points
        .map((p) => ({
            lat: Number(p.lat),
            lng: Number(p.lng),
            ts: p.timestamp ? new Date(p.timestamp).getTime() : null,
        }))
        .filter((p) => !isNaN(p.lat) && !isNaN(p.lng));

    if (valid.length < 2) return valid;

    const out = [valid[0]];

    for (let i = 1; i < valid.length; i++) {
        const prev = out[out.length - 1];
        const curr = valid[i];
        const d1 = distKm(prev, curr);

        // 1. Jitter
        if (d1 < MIN_MOVE_KM) continue;

        // 2. Teleport
        if (d1 > MAX_JUMP_KM) continue;

        // 3. Speed check (only when both timestamps exist)
        if (curr.ts && prev.ts) {
            const hours = (curr.ts - prev.ts) / 3_600_000;
            if (hours > 0 && d1 / hours > MAX_SPEED_KH) continue;
        }

        // 4. Spike / triangle look-ahead
        if (i < valid.length - 1) {
            const next = valid[i + 1];
            const d2 = distKm(curr, next);
            const d3 = distKm(prev, next);
            // curr is a detour spike: both legs short but combined >> direct distance
            if (d1 < MAX_SPIKE_KM && d2 < MAX_SPIKE_KM && (d1 + d2) > d3 * 2.5) {
                continue;
            }
        }

        out.push(curr);
    }

    return out;
};

/**
 * Thin point list to at most `maxPts` while keeping first & last.
 * Only called when OSRM is overwhelmed (very long routes).
 */
const subsample = (points, maxPts = 300) => {
    if (points.length <= maxPts) return points;
    const step = Math.ceil(points.length / maxPts);
    return points.filter((_, i) => i % step === 0 || i === points.length - 1);
};

// ─── OSRM map-matching ────────────────────────────────────────────────────────

/**
 * Route a cleaned point list along actual roads using OSRM /route.
 *
 * WHY /route INSTEAD OF /match:
 *   /match tries to snap a dense GPS trace to roads — it needs clean,
 *   high-frequency data to work. With noisy GPS it draws straight lines
 *   through buildings when it fails.
 *
 *   /route takes a list of waypoints and finds the real road path between
 *   them. It ALWAYS follows streets, regardless of GPS quality.
 *
 * STRATEGY:
 *   1. Thin the cleaned points to at most OSRM_MAX_WAYPOINTS (40).
 *      Fewer, better-spaced waypoints = more accurate road choice.
 *   2. Split into chunks of OSRM_WAYPOINTS_PER_CALL with 1-point overlap
 *      so adjacent chunks share an endpoint and join seamlessly.
 *   3. Concatenate the road geometry from all chunks.
 */
const fetchSnappedRoute = async (points) => {
    if (points.length < 2) return points.map((p) => [p.lat, p.lng]);

    // Thin GPS down to a manageable set of waypoints
    const waypoints = subsample(points, OSRM_MAX_WAYPOINTS);

    // Build chunks with 1-point overlap for seamless joining
    const chunks = [];
    for (let i = 0; i < waypoints.length; i += OSRM_WAYPOINTS_PER_CALL - 1) {
        const chunk = waypoints.slice(i, i + OSRM_WAYPOINTS_PER_CALL);
        if (chunk.length >= 2) chunks.push(chunk);
    }

    const finalPath = [];

    for (const chunk of chunks) {
        try {
            const coords = chunk.map((p) => `${p.lng},${p.lat}`).join(";");

            const res = await fetch(
                `https://router.project-osrm.org/route/v1/driving/${coords}` +
                `?overview=full&geometries=geojson&continue_straight=false`
            );
            const data = await res.json();

            if (!data.routes?.length) continue;

            const routed = data.routes[0].geometry.coordinates.map(
                ([lng, lat]) => [lat, lng]
            );

            // Drop first point on subsequent chunks — shared with previous chunk's last
            finalPath.push(...(finalPath.length ? routed.slice(1) : routed));
        } catch (err) {
            console.warn("OSRM route chunk failed:", err.message);
        }
    }

    // Last-resort fallback only if OSRM is completely unreachable
    return finalPath.length ? finalPath : points.map((p) => [p.lat, p.lng]);
};

// ─── Component ────────────────────────────────────────────────────────────────

const MapDrivers = () => {
    const userEmail = sessionStorage.getItem("userEmail");
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);

    const processDriver = useCallback(async (driver) => {
        const cleaned = cleanGPSPoints(driver.route ?? []);
        const thinned = subsample(cleaned, 300);
        const snappedPath = await fetchSnappedRoute(thinned);
        return { ...driver, snappedPath };
    }, []);

    const fetchDrivers = useCallback(async () => {
        try {
            const res = await fetch(`/api/dashboard/allDriversRoutes/${userEmail}`);
            const data = await res.json();

            const processed = await Promise.all(
                (data.drivers ?? []).map(processDriver)
            );

            setDrivers(processed);
        } catch (err) {
            logger.log(`fetchDrivers error: ${err}`, "ERROR");
        } finally {
            setLoading(false);
        }
    }, [userEmail, processDriver]);

    useEffect(() => {
        fetchDrivers();
        const id = setInterval(fetchDrivers, 30_000);
        return () => clearInterval(id);
    }, [fetchDrivers]);

    return (
        <>
            <MapNav />

            <div id="map">
                <div className="map-wrapper">
                    <div className="map-header">
                        <div>
                            <h3 className="map-heading">Live Driver Tracking</h3>
                            <p className="map-subheading">
                                {loading ? "Loading…" : `${drivers.length} active drivers`}
                            </p>
                        </div>
                        <span className="live-badge">LIVE</span>
                    </div>

                    <MapContainer
                        center={[22.9734, 78.6569]}
                        zoom={5}
                        style={{ height: "92%", width: "100%" }}
                    >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <FitBounds drivers={drivers} />

                        {drivers.map((driver, idx) => {
                            const color = ROUTE_COLORS[idx % ROUTE_COLORS.length];
                            const path = driver.snappedPath ?? [];
                            if (path.length < 2) return null;

                            return (
                                <React.Fragment key={driver.email}>
                                    {/* Shadow stroke for depth */}
                                    <Polyline
                                        positions={path}
                                        pathOptions={{ color: "#000", weight: 6, opacity: 0.07 }}
                                    />
                                    {/* Coloured route */}
                                    <Polyline
                                        positions={path}
                                        pathOptions={{
                                            color,
                                            weight: 4,
                                            opacity: 0.9,
                                            lineCap: "round",
                                            lineJoin: "round",
                                        }}
                                    />

                                    {/* Start dot */}
                                    <Marker position={path[0]} icon={startIcon} />

                                    {/* Driver marker (most-recent position) */}
                                    <Marker
                                        position={path[path.length - 1]}
                                        icon={getDriverIcon(
                                            (driver.vehicle ?? "DEFAULT").toUpperCase(),
                                            color
                                        )}
                                    >
                                        <Tooltip direction="top" offset={[0, -42]}>
                                            <div className="driver-card">
                                                <strong>{driver.name}</strong>
                                                <br />
                                                {driver.vehicle}
                                            </div>
                                        </Tooltip>
                                    </Marker>
                                </React.Fragment>
                            );
                        })}
                    </MapContainer>
                </div>
            </div>
        </>
    );
};

export default MapDrivers;